import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  Check,
  Clipboard,
  Code2,
  Database,
  Eye,
  EyeOff,
  FileKey2,
  KeyRound,
  Loader2,
  Network,
  Power,
  RefreshCw,
  Save,
  ServerCog,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import api, { ENDPOINTS } from '../config/api';
import PageHeader from '../components/PageHeader';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const emptyConfiguration = {
  frontendUrl: '',
  runtimeEnvironment: '',
  apiPort: '',
  agentPath: '',
  javaBin: '',
  storageBaseDir: '',
  dbPath: '',
  jwtSecretConfigured: null,
  restartRequired: null,
  updatedBy: '',
  updatedAt: '',
};

const editableKeys = ['frontendUrl', 'runtimeEnvironment', 'agentPath', 'javaBin'];
const JWT_SECRET_MASK = '••••••••••••••••';
const RESTART_TIMEOUT_MS = 60000;
const sleep = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));

const normalizeEditable = (configuration) => editableKeys.reduce((result, key) => {
  result[key] = configuration[key] || '';
  return result;
}, {});

const getErrorMessage = (error, fallback) => error?.response?.data?.error || error?.message || fallback;

const SystemConfiguration = () => {
  const [configuration, setConfiguration] = useState(emptyConfiguration);
  const [initialEditable, setInitialEditable] = useState(normalizeEditable(emptyConfiguration));
  const [jwtSecret, setJwtSecret] = useState('');
  const [jwtSecretChanged, setJwtSecretChanged] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [loadingSecret, setLoadingSecret] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [restartDialogOpen, setRestartDialogOpen] = useState(false);
  const [restartPhase, setRestartPhase] = useState('idle');
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const loadConfiguration = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const response = await api.get(ENDPOINTS.SYSTEM_CONFIGURATION);
      const nextConfiguration = { ...emptyConfiguration, ...response.data.configuration };
      setConfiguration(nextConfiguration);
      setInitialEditable(normalizeEditable(nextConfiguration));
      setJwtSecret('');
      setJwtSecretChanged(false);
      setShowSecret(false);
    } catch (error) {
      const message = getErrorMessage(error, '系统配置加载失败');
      setLoadError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfiguration();
  }, [loadConfiguration]);

  const isDirty = useMemo(() => {
    if (jwtSecretChanged) return true;
    return editableKeys.some((key) => (configuration[key] || '') !== (initialEditable[key] || ''));
  }, [configuration, initialEditable, jwtSecretChanged]);

  const updateField = (field) => (event) => {
    setConfiguration((current) => ({ ...current, [field]: event.target.value }));
  };

  const generateSecret = () => {
    const bytes = new Uint8Array(36);
    window.crypto.getRandomValues(bytes);
    const generated = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    setJwtSecret(generated);
    setJwtSecretChanged(true);
    setShowSecret(true);
    toast.success('已生成新的高强度密钥，保存后需重启 API');
  };

  const toggleSecretVisibility = async () => {
    if (showSecret) {
      setShowSecret(false);
      return;
    }

    if (jwtSecret || !configuration.jwtSecretConfigured) {
      setShowSecret(true);
      return;
    }

    setLoadingSecret(true);
    try {
      const response = await api.get(ENDPOINTS.SYSTEM_CONFIGURATION_SECRET);
      setJwtSecret(response.data.jwtSecret || '');
      setJwtSecretChanged(false);
      setShowSecret(true);
    } catch (error) {
      toast.error(getErrorMessage(error, 'JWT 签名密钥读取失败'));
    } finally {
      setLoadingSecret(false);
    }
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!isDirty || saving) return;

    setSaving(true);
    try {
      const payload = {
        ...normalizeEditable(configuration),
        jwtSecret: jwtSecretChanged ? jwtSecret.trim() : '',
      };
      const response = await api.put(ENDPOINTS.SYSTEM_CONFIGURATION, payload);
      const nextConfiguration = { ...emptyConfiguration, ...response.data.configuration };
      setConfiguration(nextConfiguration);
      setInitialEditable(normalizeEditable(nextConfiguration));
      setJwtSecret('');
      setJwtSecretChanged(false);
      setShowSecret(false);
      toast.success(response.data.message || '系统配置已保存');
    } catch (error) {
      toast.error(getErrorMessage(error, '系统配置保存失败'));
    } finally {
      setSaving(false);
    }
  };

  const copyRestartCommand = async () => {
    try {
      await navigator.clipboard.writeText('docker compose restart api');
      toast.success('重启命令已复制');
    } catch {
      toast.error('复制失败，请手动执行 docker compose restart api');
    }
  };

  const waitForRestart = async () => {
    const deadline = Date.now() + RESTART_TIMEOUT_MS;

    // 后端会在响应发送后延迟退出，先避开旧进程仍可响应的时间窗口。
    await sleep(1600);

    while (Date.now() < deadline) {
      try {
        const response = await api.get(ENDPOINTS.SYSTEM_CONFIGURATION, {
          timeout: 4000,
          headers: { 'Cache-Control': 'no-cache' },
        });
        const nextConfiguration = { ...emptyConfiguration, ...response.data.configuration };

        // 旧进程仍会报告 restartRequired=true；只有新进程加载数据库配置后才算恢复。
        if (!nextConfiguration.restartRequired) {
          if (mountedRef.current) {
            setConfiguration(nextConfiguration);
            setInitialEditable(normalizeEditable(nextConfiguration));
            setJwtSecret('');
            setJwtSecretChanged(false);
            setShowSecret(false);
          }
          return true;
        }
      } catch (error) {
        // 修改 JWT 密钥后，旧会话会失效，全局 401 处理会自动返回登录页。
        if (error.isSessionExpired) return false;
      }

      await sleep(1400);
    }

    throw new Error('等待 API 恢复超时，请检查容器运行状态');
  };

  const handleRestart = async () => {
    if (restartPhase !== 'idle' || isDirty) return;

    setRestartPhase('requesting');
    try {
      const response = await api.post(ENDPOINTS.SYSTEM_RESTART);
      setRestartDialogOpen(false);
      setRestartPhase('waiting');
      toast.success(response.data.message || 'API 重启任务已提交');

      const recovered = await waitForRestart();
      if (recovered && mountedRef.current) {
        setRestartPhase('idle');
        toast.success('API 已重新启动，新配置现已生效');
      } else if (mountedRef.current) {
        setRestartPhase('idle');
      }
    } catch (error) {
      if (mountedRef.current) setRestartPhase('idle');
      if (!error.isSessionExpired) {
        toast.error(getErrorMessage(error, 'API 自动重启失败，请使用手动命令重试'));
      }
    }
  };

  const updatedAt = configuration.updatedAt
    ? new Intl.DateTimeFormat('zh-CN', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(configuration.updatedAt))
    : '尚未记录';
  const hasJwtSecret = configuration.jwtSecretConfigured || Boolean(jwtSecret);
  const restarting = restartPhase !== 'idle';

  return (
    <div className="page-stack configuration-page">
      <PageHeader
        title="系统配置"
        description="集中管理运行参数、安全密钥与授权引擎路径。配置保存在数据目录中，不再依赖 Compose 注入应用参数。"
        icon={SlidersHorizontal}
        badge="管理员专属"
        actions={
          <Button variant="outline" size="sm" onClick={loadConfiguration} disabled={loading || saving}>
            <RefreshCw className={loading ? 'configuration-spin' : ''} />刷新
          </Button>
        }
      />

      {loading ? (
        <div className="configuration-loading" aria-live="polite">
          <Loader2 className="configuration-spin" />
          <div><strong>正在读取系统配置</strong><span>正在连接后台配置存储…</span></div>
        </div>
      ) : loadError ? (
        <section className="configuration-error" role="alert">
          <span className="configuration-error__icon"><AlertTriangle /></span>
          <div>
            <strong>无法读取系统配置</strong>
            <p>{loadError}</p>
            <small>页面不会使用本地默认值代替后台数据，请确认 API 服务与管理员权限后重试。</small>
          </div>
          <Button type="button" variant="outline" onClick={loadConfiguration}>
            <RefreshCw />重新加载
          </Button>
        </section>
      ) : (
        <form className="configuration-layout" onSubmit={handleSave}>
          <div className="configuration-main">
            <section className="configuration-card">
              <div className="configuration-card__header">
                <span className="configuration-card__icon"><Network /></span>
                <div><h2>应用访问</h2><p>管理前端访问来源与后端运行模式。</p></div>
                <Badge variant="info">重启生效</Badge>
              </div>
              <div className="configuration-fields configuration-fields--access">
                <label className="configuration-field">
                  <span>前端访问地址 <small>CORS Origin</small></span>
                  <Input value={configuration.frontendUrl} onChange={updateField('frontendUrl')} placeholder="http://localhost:3000" autoComplete="url" />
                  <em>请输入包含协议和端口的完整地址，例如 https://license.example.com。</em>
                </label>
                <label className="configuration-field">
                  <span>运行环境</span>
                  <select value={configuration.runtimeEnvironment} onChange={updateField('runtimeEnvironment')}>
                    <option value="production">Production</option>
                    <option value="development">Development</option>
                    <option value="test">Test</option>
                  </select>
                </label>
              </div>
            </section>

            <section className="configuration-card">
              <div className="configuration-card__header">
                <span className="configuration-card__icon configuration-card__icon--security"><ShieldCheck /></span>
                <div><h2>签名与会话安全</h2><p>JWT 密钥用于签署登录会话，默认隐藏明文，管理员可按需查看。</p></div>
                <Badge variant="info">敏感配置</Badge>
              </div>
              <div className="configuration-secret-grid">
                <label className="configuration-field">
                  <span>JWT 签名密钥</span>
                  <div className="configuration-input-action">
                    <Input
                      type={showSecret ? 'text' : 'password'}
                      value={showSecret ? jwtSecret : ''}
                      onChange={(event) => {
                        setJwtSecret(event.target.value);
                        setJwtSecretChanged(true);
                      }}
                      placeholder={hasJwtSecret ? JWT_SECRET_MASK : '至少输入 32 个字符'}
                      className={hasJwtSecret && !showSecret ? 'configuration-secret-input--configured' : ''}
                      readOnly={!showSecret}
                      autoComplete="new-password"
                    />
                    <button type="button" onClick={toggleSecretVisibility} disabled={loadingSecret} aria-label={showSecret ? '隐藏密钥' : '显示密钥'}>
                      {loadingSecret ? <Loader2 className="configuration-spin" /> : showSecret ? <EyeOff /> : <Eye />}
                    </button>
                  </div>
                  <em>修改密钥后，API 重启时所有现有登录会话会失效。</em>
                </label>
                <button type="button" className="configuration-generate" onClick={generateSecret}>
                  <span><Sparkles /></span>
                  <div><strong>生成安全密钥</strong><small>创建 72 位随机十六进制密钥</small></div>
                </button>
              </div>
            </section>

            <section className="configuration-card">
              <div className="configuration-card__header">
                <span className="configuration-card__icon configuration-card__icon--runtime"><Code2 /></span>
                <div><h2>授权引擎</h2><p>指定 Java 运行时与 atlassian-agent.jar；留空时由服务自动检测。</p></div>
                <Badge variant="info">Java 11.0.17+</Badge>
              </div>
              <div className="configuration-fields">
                <label className="configuration-field">
                  <span>Java 可执行文件</span>
                  <Input value={configuration.javaBin} onChange={updateField('javaBin')} placeholder="自动检测，或输入 /path/to/java" />
                  <em>支持 macOS 与 Linux 的绝对路径；留空将检查 JAVA_HOME、PATH 和常见 JDK 目录。</em>
                </label>
                <label className="configuration-field">
                  <span>Agent JAR 路径</span>
                  <Input value={configuration.agentPath} onChange={updateField('agentPath')} placeholder="自动检测，Docker 默认已内置" />
                  <em>Docker 镜像默认使用 /opt/.atlassian-agent/atlassian-agent.jar。</em>
                </label>
              </div>
            </section>
          </div>

          <aside className="configuration-rail">
            <section className={`configuration-status ${configuration.restartRequired ? 'is-pending' : 'is-synced'}`}>
              <div className="configuration-status__top">
                <span>{restarting ? <Loader2 className="configuration-spin" /> : configuration.restartRequired ? <AlertTriangle /> : <Check />}</span>
                <div>
                  <small>配置状态</small>
                  <strong>{restarting ? 'API 正在重新启动' : configuration.restartRequired ? '等待 API 重启' : '已与运行实例同步'}</strong>
                </div>
              </div>
              <p>{restarting ? '服务会短暂不可用，页面正在自动等待新进程恢复。' : configuration.restartRequired ? '新配置已写入数据库，但当前进程仍在使用启动时参数。' : '当前没有等待生效的配置变更。'}</p>
              {configuration.restartRequired && (
                <div className="configuration-restart-actions">
                  <Button
                    type="button"
                    className="configuration-restart-button"
                    onClick={() => setRestartDialogOpen(true)}
                    disabled={restarting || isDirty}
                  >
                    {restarting ? <Loader2 className="configuration-spin" /> : <Power />}
                    {restarting ? '等待服务恢复…' : '立即重启 API'}
                  </Button>
                  <button type="button" className="configuration-command" onClick={copyRestartCommand} disabled={restarting}>
                    <code>复制手动重启命令</code><Clipboard />
                  </button>
                  {isDirty && <small className="configuration-restart-hint">请先保存当前更改，再执行重启。</small>}
                </div>
              )}
            </section>

            <section className="configuration-summary">
              <div className="configuration-summary__heading"><ServerCog /><div><strong>当前部署</strong><span>只读启动信息</span></div></div>
              <dl>
                <div><dt><Database />数据目录</dt><dd title={configuration.storageBaseDir}>{configuration.storageBaseDir || '—'}</dd></div>
                <div><dt><FileKey2 />数据库文件</dt><dd title={configuration.dbPath}>{configuration.dbPath || '—'}</dd></div>
                <div><dt><Network />API 内部端口</dt><dd>{configuration.apiPort || '未提供'}</dd></div>
                <div><dt><KeyRound />密钥状态</dt><dd>{configuration.jwtSecretConfigured ? '已安全配置' : '尚未配置'}</dd></div>
              </dl>
              <p>数据目录、数据库路径和容器内部端口属于部署级参数，为避免存储迁移或代理失联，仅在此处展示。</p>
            </section>

            <section className="configuration-meta">
              <span>最后更新</span>
              <strong>{updatedAt}</strong>
              <small>操作人：{configuration.updatedBy || '未记录'}</small>
            </section>

            <div className="configuration-save-panel">
              <div>
                <span className={isDirty ? 'is-dirty' : ''}>{isDirty ? '有未保存的更改' : '所有更改已保存'}</span>
                <small>保存后可在上方一键重启 API。</small>
              </div>
              <Button type="submit" disabled={!isDirty || saving}>
                {saving ? <Loader2 className="configuration-spin" /> : <Save />}
                {saving ? '正在保存…' : '保存配置'}
              </Button>
            </div>
          </aside>
        </form>
      )}

      <AlertDialog.Root
        open={restartDialogOpen}
        onOpenChange={(open) => {
          if (!restarting) setRestartDialogOpen(open);
        }}
      >
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="configuration-restart-dialog__overlay" />
          <AlertDialog.Content className="configuration-restart-dialog">
            <div className="configuration-restart-dialog__icon" aria-hidden="true">
              <Power />
            </div>
            <div className="configuration-restart-dialog__heading">
              <span>服务操作确认</span>
              <AlertDialog.Title>立即重启 API？</AlertDialog.Title>
            </div>
            <AlertDialog.Description className="configuration-restart-dialog__description">
              API 会短暂不可用。Docker 将自动拉起新进程，页面会持续检测服务状态，无需登录服务器执行命令。
            </AlertDialog.Description>
            <div className="configuration-restart-dialog__notice">
              <AlertTriangle aria-hidden="true" />
              <div>
                <strong>会话可能需要重新登录</strong>
                <span>如果本次修改了 JWT 签名密钥，现有登录状态会在重启后自动失效。</span>
              </div>
            </div>
            <div className="configuration-restart-dialog__actions">
              <AlertDialog.Cancel asChild>
                <Button type="button" variant="outline" disabled={restarting}>暂不重启</Button>
              </AlertDialog.Cancel>
              <AlertDialog.Action asChild>
                <Button
                  type="button"
                  className="configuration-restart-dialog__confirm"
                  disabled={restarting}
                  onClick={(event) => {
                    event.preventDefault();
                    handleRestart();
                  }}
                >
                  {restartPhase === 'requesting' ? <Loader2 className="configuration-spin" /> : <Power />}
                  {restartPhase === 'requesting' ? '正在提交…' : '确认重启'}
                </Button>
              </AlertDialog.Action>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </div>
  );
};

export default SystemConfiguration;
